const PROJECT_MANAGER_ROLES = new Set(['tenant_admin', 'xunmei_admin'])
const SPACE_ADMIN_ROLE = 'space_admin'

function roleCodes(userState = {}) {
  const fromState = Array.isArray(userState.roleCodes) ? userState.roleCodes : []
  const fromInfo = userState.userInfo && Array.isArray(userState.userInfo.roleCodes)
    ? userState.userInfo.roleCodes
    : []
  return [...new Set([...fromState, ...fromInfo]
    .map(code => String(code || '').trim().toLowerCase())
    .filter(Boolean))]
}

function isPlatformSuperAdmin(userState = {}) {
  return Boolean(userState.userInfo && userState.userInfo.isSuperAdmin)
}

function isTenantProjectManager(userState = {}) {
  return !isPlatformSuperAdmin(userState) && roleCodes(userState).some(code => PROJECT_MANAGER_ROLES.has(code))
}

function isProjectManager(userState = {}) {
  return isPlatformSuperAdmin(userState) || isTenantProjectManager(userState)
}

function isSpaceAdmin(userState = {}) {
  return roleCodes(userState).includes(SPACE_ADMIN_ROLE)
}

function initialRoomAccess(userState = {}) {
  if (isProjectManager(userState)) {
    return { resolved: true, unrestricted: true, rooms: {} }
  }
  if (isSpaceAdmin(userState)) {
    // 小管理员在接口成功返回前保持无房间，避免短暂展示越权空间。
    return { resolved: false, unrestricted: false, rooms: {} }
  }
  // 未知/历史角色不再按“兼容管理员”放行，等待后台迁移或平台超管明确修复角色。
  return { resolved: true, unrestricted: false, rooms: {} }
}

function normalizeMode(mode) {
  const value = String(mode || '').trim().toUpperCase()
  return value === 'READ_ONLY' || value === 'READ_WRITE' ? value : 'NONE'
}

function normalizeRooms(rooms) {
  if (!rooms || typeof rooms !== 'object' || Array.isArray(rooms)) return {}
  return Object.entries(rooms).reduce((result, [roomId, mode]) => {
    const normalized = normalizeMode(mode)
    if (normalized !== 'NONE') result[String(roomId)] = normalized
    return result
  }, {})
}

function normalizeRoomAccess(response, userState = {}) {
  if (isProjectManager(userState)) return initialRoomAccess(userState)
  if (!isSpaceAdmin(userState)) return initialRoomAccess(userState)
  const data = response && response.data !== undefined ? response.data : response
  if (!data || typeof data !== 'object') return initialRoomAccess(userState)

  if (typeof data.unrestricted === 'boolean') {
    return {
      resolved: true,
      unrestricted: false,
      rooms: normalizeRooms(data.rooms)
    }
  }

  if (data.roomId != null || data.accessMode != null) {
    const mode = normalizeMode(data.accessMode)
    const rooms = mode === 'NONE' || data.roomId == null
      ? {}
      : { [String(data.roomId)]: mode }
    return { resolved: true, unrestricted: false, rooms }
  }

  return initialRoomAccess(userState)
}

function roomAccessMode(access, roomId) {
  if (access && access.unrestricted) return 'READ_WRITE'
  if (!access || roomId == null) return 'NONE'
  return normalizeMode((access.rooms || {})[String(roomId)])
}

function canReadRoom(access, roomId) {
  return roomAccessMode(access, roomId) !== 'NONE'
}

function canControlRoom(access, roomId) {
  return roomAccessMode(access, roomId) === 'READ_WRITE'
}

function roomContextCapabilities(access, roomId, userState = {}) {
  const projectManager = isProjectManager(userState)
  const spaceAdmin = isSpaceAdmin(userState)
  const readable = projectManager || (spaceAdmin && canReadRoom(access, roomId))
  const writable = projectManager || (spaceAdmin && canControlRoom(access, roomId))
  return {
    canViewDevices: readable,
    canManagePersonnel: writable,
    canManageAssets: writable,
    canManagePermissions: projectManager
  }
}

function filterAuthorizedRooms(rooms, access) {
  const list = Array.isArray(rooms) ? rooms : []
  if (access && access.unrestricted) return list
  return list.filter(room => room && canReadRoom(access, room.id != null ? room.id : room.zoneId))
}

function accessModeLabel(mode) {
  const normalized = normalizeMode(mode)
  if (normalized === 'READ_WRITE') return '可读写'
  if (normalized === 'READ_ONLY') return '只读'
  return '无房间权限'
}

module.exports = {
  accessModeLabel,
  canControlRoom,
  canReadRoom,
  filterAuthorizedRooms,
  initialRoomAccess,
  isPlatformSuperAdmin,
  isProjectManager,
  isSpaceAdmin,
  isTenantProjectManager,
  normalizeRoomAccess,
  roomContextCapabilities,
  roomAccessMode,
  roleCodes
}
